import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Crown, Phone, Mail, Check, ArrowRight, Star, Zap, TrendingDown, Sparkles, Calculator } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_ORDER = ['starter', 'plus', 'premier', 'essentials', 'executive', 'black_card'];

const TIER_ACCENT = {
  starter:    { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700', pill: 'bg-gray-200 text-gray-700' },
  plus:       { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', pill: 'bg-blue-100 text-blue-700' },
  premier:    { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', pill: 'bg-purple-100 text-purple-700' },
  essentials: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', pill: 'bg-emerald-100 text-emerald-700' },
  executive:  { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-800', pill: 'bg-amber-100 text-amber-800', featured: true },
  black_card: { bg: 'bg-gradient-to-br from-gray-900 to-black', border: 'border-black', text: 'text-white', pill: 'bg-red-600 text-white', dark: true },
};

const Membership = () => {
  const navigate = useNavigate();
  const [tiers, setTiers] = useState({});
  const [calcHours, setCalcHours] = useState(8);

  useEffect(() => {
    fetch(`${API}/api/command-center/ops/membership-tiers`)
      .then(r => r.json()).then(d => setTiers(d.tiers || {}))
      .catch(() => {});
  }, []);

  // Returning-client savings calculator
  const calc = useMemo(() => {
    if (!tiers.retail) return [];
    const retail = tiers.retail.hourly_rate;
    return TIER_ORDER.map(k => {
      const t = tiers[k]; if (!t) return null;
      const monthly = t.monthly_fee;
      const hourlySpend = t.hourly_rate * calcHours;
      const total = monthly + hourlySpend;
      const retailTotal = retail * calcHours;
      const saved = retailTotal - total;
      const bankCovered = Math.min(t.hours_included || 0, calcHours);
      const bankValue = bankCovered * retail;
      return { key: k, ...t, hourlySpend, total, saved, bankCovered, bankValue, retailTotal };
    }).filter(Boolean);
  }, [tiers, calcHours]);

  if (!tiers.starter) return <div className="min-h-screen flex items-center justify-center text-gray-400 text-sm">Loading membership tiers…</div>;

  return (
    <div className="bg-white" data-testid="membership-page">
      {/* HERO */}
      <section className="relative bg-gradient-to-br from-black via-gray-950 to-black text-white py-24 sm:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,_rgba(220,38,38,0.18),_transparent_60%)]" />
        <div className="max-w-5xl mx-auto px-6 relative z-10 text-center">
          <p className="text-red-500 text-xs font-bold tracking-[0.3em] uppercase mb-4 flex items-center justify-center gap-2"><Crown className="h-3.5 w-3.5" /> ACE MEMBERSHIP</p>
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black tracking-tight leading-[1.05]" data-testid="membership-h1">
            You've Already <br /><em className="not-italic text-red-500">Chosen ACE.</em><br />
            Now Choose <em className="not-italic text-red-500">Smarter.</em>
          </h1>
          <p className="text-base sm:text-lg text-gray-400 mt-6 max-w-2xl mx-auto leading-relaxed">
            If you're booking with ACE more than once a month, membership pays for itself before the first ride is over. Lower hourly rates, included hours, fleet access — without the retail markup.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
            <a href="tel:4045438930" className="inline-flex items-center justify-center h-12 px-6 rounded-lg bg-red-600 hover:bg-red-700 text-sm font-bold" data-testid="hero-call-btn">
              <Phone className="h-4 w-4 mr-2" /> (404) 543-8930
            </a>
            <a href="mailto:JD@yourautoconcierge.com" className="inline-flex items-center justify-center h-12 px-6 rounded-lg border border-gray-700 hover:bg-white/5 text-sm font-bold" data-testid="hero-email-btn">
              <Mail className="h-4 w-4 mr-2" /> Talk to JD
            </a>
          </div>
        </div>
      </section>

      {/* SAVINGS CALCULATOR */}
      <section className="py-16 sm:py-20 bg-gray-50" data-testid="savings-calculator">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-[11px] text-red-600 font-bold tracking-[0.25em] mb-3 flex items-center gap-2"><Calculator className="h-3.5 w-3.5" /> REAL NUMBERS · YOUR USAGE</p>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight leading-[1.1] mb-3">If You Ride <em className="not-italic text-red-500">{calcHours} Hours / Month</em></h2>
          <p className="text-sm text-gray-500 mb-6">Drag the slider — see exactly what each tier saves you vs paying retail every time.</p>

          {/* Slider */}
          <div className="bg-white p-5 rounded-xl border border-gray-200 mb-6">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-bold text-gray-700">Monthly hours used</label>
              <span className="text-2xl font-black text-red-600">{calcHours}h</span>
            </div>
            <input type="range" min="2" max="40" step="1" value={calcHours}
              onChange={e => setCalcHours(Number(e.target.value))}
              className="w-full accent-red-600" data-testid="calc-slider" />
            <div className="flex justify-between text-[10px] text-gray-400 mt-1">
              <span>Occasional · 2h</span><span>Regular · 8h</span><span>Power user · 40h</span>
            </div>
          </div>

          <Card className="border-gray-200 overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-gray-100 text-left">
                <tr className="text-[10px] uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-2.5">Tier</th>
                  <th className="px-4 py-2.5 text-right">Monthly</th>
                  <th className="px-4 py-2.5 text-right">Hourly</th>
                  <th className="px-4 py-2.5 text-right">Hours Bank</th>
                  <th className="px-4 py-2.5 text-right">Your Total</th>
                  <th className="px-4 py-2.5 text-right">You Save vs Retail</th>
                </tr>
              </thead>
              <tbody>
                {calc.map(r => (
                  <tr key={r.key} className={`border-t border-gray-100 ${TIER_ACCENT[r.key]?.featured ? 'bg-amber-50/40' : ''}`} data-testid={`calc-row-${r.key}`}>
                    <td className="px-4 py-3"><span className={`inline-block text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded ${TIER_ACCENT[r.key]?.pill}`}>{r.name}</span></td>
                    <td className="px-4 py-3 text-right font-bold">${r.monthly_fee}</td>
                    <td className="px-4 py-3 text-right">${r.hourly_rate}/hr</td>
                    <td className="px-4 py-3 text-right">{r.hours_included ? `${r.hours_included}h ${r.hours_scope || ''}` : '—'}</td>
                    <td className="px-4 py-3 text-right font-bold">${r.total.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right">
                      {r.saved > 0
                        ? <span className="font-black text-emerald-600">−${Math.round(r.saved).toLocaleString()}</span>
                        : r.saved === 0
                          ? <span className="text-gray-400">break even</span>
                          : <span className="text-gray-400">+${Math.abs(Math.round(r.saved))}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
          <p className="text-[10px] text-gray-400 mt-3 italic">Assumes ${tiers.retail?.hourly_rate}/hr retail rate. Numbers based on listed Lite & Full-Access tier hourly rates. Hours bank value shown for Full-Access tiers (Essentials+).</p>
        </div>
      </section>

      {/* 6-TIER CARDS */}
      <section className="py-16 sm:py-24" id="tiers" data-testid="tier-cards-section">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-[11px] text-red-600 font-bold tracking-[0.25em] mb-3">SIX TIERS · TWO TRACKS</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.1] mb-3">Pick Your Track.<br /><em className="not-italic text-red-500">Pay Less. Ride More.</em></h2>
          <p className="text-sm sm:text-base text-gray-500 max-w-2xl">Lite tiers ($25–$195) unlock lower hourly rates. Full-Access tiers ($297–$1,497) add monthly hours banks + production-day credits.</p>

          {/* Lite Track */}
          <div className="mt-10">
            <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-3">Lite Concierge Track · Discounts Only</p>
            <div className="grid sm:grid-cols-3 gap-4">
              {['starter', 'plus', 'premier'].map(k => <TierCard key={k} k={k} t={tiers[k]} retail={tiers.retail?.hourly_rate} />)}
            </div>
          </div>

          {/* Full-Access Track */}
          <div className="mt-10">
            <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-3">Full-Access Track · Discounts + Monthly Hours Banks</p>
            <div className="grid sm:grid-cols-3 gap-4">
              {['essentials', 'executive', 'black_card'].map(k => <TierCard key={k} k={k} t={tiers[k]} retail={tiers.retail?.hourly_rate} />)}
            </div>
          </div>
        </div>
      </section>

      {/* WHY IT WORKS */}
      <section className="bg-gray-50 py-16 sm:py-20" data-testid="why-it-works">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-[11px] text-red-600 font-bold tracking-[0.25em] mb-3">WHY MEMBERSHIP CHANGES EVERYTHING</p>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-8">Four Things You Get the Moment You Sign Up.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: TrendingDown, title: 'Lower Hourly Rates', desc: 'Retail $100/hr drops to $70-95/hr based on tier. Locked in, no surprises.' },
              { icon: Zap, title: 'Priority Dispatch', desc: 'Member calls go to the top of dispatch. Faster confirmation, better drivers, your preferred vehicle.' },
              { icon: Star, title: 'Production Hour Credits', desc: 'Full-Access tiers get 2–10 production hours/month for photo, video, or content captures of fleet vehicles.' },
              { icon: Sparkles, title: 'No Markup on Add-Ons', desc: 'Champagne, child seats, signage, custom playlists — at cost, not at markup.' },
            ].map(b => (
              <Card key={b.title} className="p-5 border-gray-200 hover:border-red-200 hover:shadow-md transition-all" data-testid={`benefit-${b.title.replace(/\W+/g, '-').toLowerCase()}`}>
                <div className="h-10 w-10 rounded-lg bg-red-600 flex items-center justify-center mb-3">
                  <b.icon className="h-5 w-5 text-white" />
                </div>
                <p className="text-base font-bold text-black mb-1.5">{b.title}</p>
                <p className="text-xs text-gray-600 leading-relaxed">{b.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-16 sm:py-20" data-testid="testimonials">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-[11px] text-red-600 font-bold tracking-[0.25em] mb-3">RETURNING CLIENTS · REAL WORDS</p>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-8">Members Don't Go Back.</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {[
              { quote: '"After three months of paying retail I did the math. Black Card pays for itself in 14 rides. Wish I had signed up day one."', name: 'D. Mitchell', tier: 'Black Card' },
              { quote: '"The production hours alone justify Executive. We shot two music videos this quarter using ACE picture cars at no add-on rate."', name: 'R. Patel', tier: 'Executive' },
              { quote: '"Plus tier saves my company $400/mo without changing how often we book. No-brainer."', name: 'S. Edwards', tier: 'Plus' },
            ].map((t, i) => (
              <Card key={i} className="p-5 border-gray-200" data-testid={`testimonial-${i}`}>
                <p className="text-sm text-gray-700 leading-relaxed italic">{t.quote}</p>
                <div className="mt-4 pt-3 border-t border-gray-100">
                  <p className="text-xs font-bold text-black">{t.name}</p>
                  <p className="text-[10px] uppercase tracking-widest text-red-600 font-bold mt-0.5">{t.tier} Member</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER CTA */}
      <section className="bg-gradient-to-br from-black via-gray-950 to-black text-white py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-black mb-3 tracking-tight">Ready to Upgrade? <em className="text-red-500 not-italic">JD Will Walk You Through It.</em></h2>
          <p className="text-sm text-gray-400 max-w-2xl mx-auto mb-8">No online checkout. Memberships are activated personally so we match the right tier to your real usage — and grandfather you into your starting rate.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="tel:4045438930" className="inline-flex items-center justify-center h-12 px-7 rounded-lg bg-red-600 hover:bg-red-700 text-sm font-bold" data-testid="footer-call-btn">
              <Phone className="h-4 w-4 mr-2" /> (404) 543-8930
            </a>
            <a href="mailto:JD@yourautoconcierge.com?subject=Membership%20Inquiry" className="inline-flex items-center justify-center h-12 px-7 rounded-lg border border-gray-700 hover:bg-white/5 text-sm font-bold" data-testid="footer-email-btn">
              <Mail className="h-4 w-4 mr-2" /> JD@YourAutoConcierge.com
            </a>
          </div>
          <p className="text-[10px] text-gray-500 mt-6">YourAutoConcierge.com · 1954 Airport Rd Suite 500, Chamblee GA</p>
        </div>
      </section>
    </div>
  );
};

const TierCard = ({ k, t, retail }) => {
  const a = TIER_ACCENT[k];
  if (!t) return null;
  return (
    <Card className={`p-6 ${a.bg} ${a.border} border-2 ${a.featured ? 'ring-2 ring-amber-400 ring-offset-2' : ''} relative`} data-testid={`tier-card-${k}`}>
      {a.featured && <span className="absolute top-3 right-3 text-[9px] font-bold uppercase tracking-widest bg-amber-500 text-white px-2 py-0.5 rounded-full">Most Popular</span>}
      <p className={`text-[10px] font-bold uppercase tracking-widest ${a.dark ? 'text-red-400' : a.text}`}>{t.name}</p>
      <p className={`text-4xl font-black mt-1 ${a.dark ? 'text-white' : 'text-black'}`}>${t.monthly_fee}<span className="text-sm font-normal text-gray-400">/mo</span></p>
      <p className={`text-xs mt-1 ${a.dark ? 'text-gray-400' : 'text-gray-500'}`}>${t.hourly_rate}/hr · save ${retail - t.hourly_rate}/hr vs retail</p>
      <ul className={`space-y-1.5 mt-4 text-xs ${a.dark ? 'text-gray-300' : 'text-gray-700'}`}>
        <li className="flex gap-2"><Check className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${a.dark ? 'text-red-400' : 'text-red-600'}`} /> {t.discount_pct}% discount on all bookings</li>
        {t.hours_included > 0 && <li className="flex gap-2"><Check className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${a.dark ? 'text-red-400' : 'text-red-600'}`} /> {t.hours_included} included hours/mo ({t.hours_scope})</li>}
        {t.production_hrs > 0 && <li className="flex gap-2"><Check className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${a.dark ? 'text-red-400' : 'text-red-600'}`} /> {t.production_hrs}h production hours/mo</li>}
        <li className="flex gap-2"><Check className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${a.dark ? 'text-red-400' : 'text-red-600'}`} /> Priority dispatch</li>
        <li className="flex gap-2"><Check className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${a.dark ? 'text-red-400' : 'text-red-600'}`} /> No markup on add-ons</li>
      </ul>
      <a href="tel:4045438930" className={`inline-flex items-center justify-center mt-5 w-full h-10 rounded text-xs font-bold ${a.dark ? 'bg-white text-black hover:bg-gray-100' : 'bg-red-600 text-white hover:bg-red-700'}`} data-testid={`tier-cta-${k}`}>
        Activate {t.name} <ArrowRight className="h-3.5 w-3.5 ml-1" />
      </a>
    </Card>
  );
};

export default Membership;
